import {Permission} from './permission';
import {TRUST_ACCOUNT_ACTIONS, TRUST_ACCOUNT_READ_ACTIONS} from './trust-account-access';

import type {TrustAccountAction} from './trust-account-access';

/**
 * The system roles granted on a single trust account, stored by key in
 * `trust_account_members.role_key`. The trustee is the account's `lawyerId`, never a member row.
 */
export enum TrustAccountMemberRole {
  OPERATOR = 'trust_account_operator',
  VIEWER = 'trust_account_viewer'
}

export const TRUST_ACCOUNT_MEMBER_ROLES: readonly TrustAccountMemberRole[] =
  Object.values(TrustAccountMemberRole);

/**
 * Actions only the account's trustee may perform — moving money out and deciding who works on the
 * account. No account role and no firm-wide scope reaches them, whatever their permissions say.
 */
export const TRUSTEE_ONLY_TRUST_ACCOUNT_ACTIONS: readonly TrustAccountAction[] = [
  Permission.PAYMENTS_PAYOUT,
  Permission.TRUST_ACCOUNTS_MANAGE_MEMBERS
];

/**
 * Actions an operator takes in the trustee's name — they need the power of attorney the trustee
 * grants when making them an operator. Adding buyers is one: it signs a supplementary declaration.
 */
export const POWER_OF_ATTORNEY_ACTIONS: readonly TrustAccountAction[] = [
  Permission.TRUST_ACCOUNTS_SIGN_DECLARATIONS,
  Permission.BUYERS_CREATE
];

/** Approving a buyer identity change re-signs a supplementary declaration, so it takes this action too. */
export const BUYER_IDENTITY_APPROVAL_ACTION = Permission.TRUST_ACCOUNTS_SIGN_DECLARATIONS;

/**
 * What each system account role may do on the account it was granted on — its permission set,
 * resolved from here at request time. A member may perform an action
 * only if their role holds it; the firm role still has to hold the permission too (the route
 * gate). An operator may do everything but the trustee-only actions; a viewer only reads.
 */
export const TRUST_ACCOUNT_MEMBER_ROLE_PERMISSIONS: Record<
  TrustAccountMemberRole,
  readonly TrustAccountAction[]
> = {
  [TrustAccountMemberRole.OPERATOR]: TRUST_ACCOUNT_ACTIONS.filter(
    action => !TRUSTEE_ONLY_TRUST_ACCOUNT_ACTIONS.includes(action)
  ),
  [TrustAccountMemberRole.VIEWER]: TRUST_ACCOUNT_READ_ACTIONS
};

export enum TrustAccountMemberKind {
  MEMBER = 'member',
  GUEST = 'guest'
}

/** Roles granted to people outside the firm, by invitation. Phase 1 is read-only. */
export enum TrustAccountGuestRole {
  VIEWER = 'trust_account_guest_viewer'
}

export const TRUST_ACCOUNT_GUEST_ROLES: readonly TrustAccountGuestRole[] =
  Object.values(TrustAccountGuestRole);

export type TrustAccountGrantRole = TrustAccountMemberRole | TrustAccountGuestRole;

export const TRUST_ACCOUNT_GRANT_ROLES: readonly TrustAccountGrantRole[] = [
  ...TRUST_ACCOUNT_MEMBER_ROLES,
  ...TRUST_ACCOUNT_GUEST_ROLES
];

export const TRUST_ACCOUNT_GUEST_ROLE_PERMISSIONS: Record<
  TrustAccountGuestRole,
  readonly TrustAccountAction[]
> = {
  [TrustAccountGuestRole.VIEWER]: TRUST_ACCOUNT_READ_ACTIONS
};

export function isTrustAccountGuestRole(role: string): role is TrustAccountGuestRole {
  return (TRUST_ACCOUNT_GUEST_ROLES as readonly string[]).includes(role);
}

export enum TrustAccountMemberEventType {
  ADDED = 'added',
  ROLE_CHANGED = 'role_changed',
  REMOVED = 'removed',
  POWER_OF_ATTORNEY_GRANTED = 'power_of_attorney_granted',
  INVITED = 'invited',
  INVITATION_RESENT = 'invitation_resent',
  INVITATION_REVOKED = 'invitation_revoked'
}

/**
 * Why a firm user can't hold a member role — keyed so the frontend renders the reason copy.
 * Derived from the firm role's data scopes: a membership only grants what the role doesn't
 * already have firm-wide, and Sollapay-assigned roles (support) are never assignable.
 */
export enum TrustAccountMemberIneligibleReason {
  NOT_ASSIGNABLE = 'not_assignable',
  ALREADY_READS_ALL = 'already_reads_all',
  NO_WRITE_ACCESS = 'no_write_access',
  ALREADY_WRITES_ALL = 'already_writes_all'
}
